import React, { useEffect, useState } from 'react'
import { brandAlert, brandConfirm } from '../../contexts/DialogContext'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, Calendar, ChevronDown, Mail, MessageSquare, Phone, RefreshCw, Trash2, Video } from 'lucide-react'
import { db } from '../../lib/db'

type Status = 'new' | 'confirmed' | 'completed' | 'archived'

interface CheckInRequest {
  id: string
  createdAt: number | string
  userId: string
  clientFullName: string
  clientEmail: string
  topic: string
  message: string
  preferredResponse: string
  status: Status
}

const STATUS_OPTIONS: Status[] = ['new', 'confirmed', 'completed', 'archived']

const statusStyles: Record<Status, string> = {
  new: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-600',
}

const toMillis = (value: number | string) => {
  if (typeof value === 'number') return value
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

const responseIcon = (preferredResponse: string) => {
  if (preferredResponse.includes('Video')) return <Video size={15} />
  if (preferredResponse.includes('phone')) return <Phone size={15} />
  if (preferredResponse.includes('Email')) return <Mail size={15} />
  return <MessageSquare size={15} />
}

const AdminCheckInRequests: React.FC = () => {
  const [requests, setRequests] = useState<CheckInRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<'all' | Status>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<string | null>(null)

  const fetchRequests = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await db.queryOnce({
        checkInRequests: {},
      })

      const rows = (data?.checkInRequests || []) as CheckInRequest[]
      rows.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt))
      setRequests(rows)
    } catch (err) {
      console.error(err)
      setError('Failed to load check-in requests from InstantDB.')
      setRequests([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRequests()
  }, [])

  const updateStatus = async (requestId: string, status: Status) => {
    setUpdatingId(requestId)
    try {
      await db.transact(
        db.tx.checkInRequests[requestId].update({ status, updatedAt: Date.now() })
      )
      setRequests((prev) =>
        prev.map((req) => (req.id === requestId ? { ...req, status } : req))
      )
    } catch (err) {
      console.error(err)
      void brandAlert({ title: 'Something went wrong', message: 'Failed to update status. Please try again.', variant: 'error' })
    } finally {
      setUpdatingId(null)
    }
  }

  const deleteRequest = async (req: CheckInRequest) => {
    const ok = await brandConfirm({
      title: 'Delete this request?',
      message: `${req.clientFullName}'s check-in request will be permanently removed. This cannot be undone — archive it instead if you may need it later.`,
      variant: 'danger',
      confirmLabel: 'Delete request',
      cancelLabel: 'Keep it',
    })
    if (!ok) return

    setUpdatingId(req.id)
    try {
      await db.transact(db.tx.checkInRequests[req.id].delete())
      setRequests((prev) => prev.filter((r) => r.id !== req.id))
      setExpandedId((prev) => (prev === req.id ? null : prev))
    } catch (err) {
      console.error(err)
      void brandAlert({ title: 'Something went wrong', message: 'Failed to delete the request. Please try again.', variant: 'error' })
    } finally {
      setUpdatingId(null)
    }
  }

  const deleteAllArchived = async () => {
    const archived = requests.filter((r) => r.status === 'archived')
    if (archived.length === 0) return

    const ok = await brandConfirm({
      title: `Delete ${archived.length} archived request${archived.length === 1 ? '' : 's'}?`,
      message: 'Every archived check-in request will be permanently removed. This cannot be undone.',
      variant: 'danger',
      confirmLabel: 'Delete them all',
      cancelLabel: 'Cancel',
    })
    if (!ok) return

    try {
      await db.transact(
        archived.map((r) => db.tx.checkInRequests[r.id].delete())
      )
      const archivedIds = new Set(archived.map((r) => r.id))
      setRequests((prev) => prev.filter((r) => !archivedIds.has(r.id)))
      setExpandedId((prev) => (prev && archivedIds.has(prev) ? null : prev))
    } catch (err) {
      console.error(err)
      void brandAlert({ title: 'Something went wrong', message: 'Failed to delete the archived requests. Please try again.', variant: 'error' })
    }
  }

  const filtered = requests.filter((req) =>
    filter === 'all' ? true : req.status === filter
  )

  const newCount = requests.filter((r) => r.status === 'new').length
  const archivedCount = requests.filter((r) => r.status === 'archived').length

  return (
    <div className="panel-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-ink mb-4"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="eyebrow mb-3">Admin</p>
              <h1 className="panel-title">Check-In Requests</h1>
              <p className="mt-1 text-gray-600">
                {newCount} new · {requests.length} total
              </p>
            </div>
            <button
              type="button"
              onClick={fetchRequests}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-cream"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {(['all', ...STATUS_OPTIONS] as const).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setFilter(status)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${
                filter === status
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-400'
              }`}
            >
              {status}
            </button>
          ))}
          {filter === 'archived' && archivedCount > 0 && (
            <button
              type="button"
              onClick={deleteAllArchived}
              className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
            >
              <Trash2 size={15} /> Delete all archived ({archivedCount})
            </button>
          )}
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gold" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center text-gray-500">
            No check-in requests{filter !== 'all' ? ` with status "${filter}"` : ''} yet.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((req) => {
              const open = expandedId === req.id
              return (
                <motion.div
                  key={req.id}
                  layout
                  className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedId(open ? null : req.id)}
                    className="w-full text-left px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-semibold text-ink">{req.clientFullName}</h3>
                        <span className={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${statusStyles[req.status]}`}>
                          {req.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{req.clientEmail} · {req.topic}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-1.5">
                        {responseIcon(req.preferredResponse)}
                        {req.preferredResponse}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar size={15} />
                        {new Date(toMillis(req.createdAt)).toLocaleDateString()}
                      </span>
                      <ChevronDown
                        size={18}
                        className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </button>

                  <AnimatePresence>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-gray-100"
                      >
                        <div className="px-5 py-5 grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Topic</p>
                            <p className="text-gray-800">{req.topic}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Preferred Response</p>
                            <p className="text-gray-800">{req.preferredResponse}</p>
                          </div>
                          <div className="md:col-span-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Message</p>
                            <p className="text-gray-800 whitespace-pre-wrap">{req.message}</p>
                          </div>
                          <div className="md:col-span-2 flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100">
                            <a
                              href={`mailto:${req.clientEmail}`}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200"
                            >
                              <Mail size={15} /> Email
                            </a>
                            <button
                              type="button"
                              disabled={updatingId === req.id}
                              onClick={() => deleteRequest(req)}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                            >
                              <Trash2 size={15} /> Delete
                            </button>
                            <div className="flex items-center gap-2 ml-auto">
                              <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                Status
                              </label>
                              <select
                                value={req.status}
                                disabled={updatingId === req.id}
                                onChange={(e) => updateStatus(req.id, e.target.value as Status)}
                                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                              >
                                {STATUS_OPTIONS.map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <p className="md:col-span-2 text-xs text-gray-400">
                            Submitted {new Date(toMillis(req.createdAt)).toLocaleString()}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminCheckInRequests
